<template>
  <VSearchable
    class="v-searchable-thumbnail-list"
    :search="search"
    :placeholder="placeholder"
    :disabled="disabled"
    :loading="loading"
    :count="count"
    :auto-load="autoLoad"
    :page-count="options.length"
    @update:search="$emit('update:search', $event)"
    @load-more="$emit('load-more', $event)"
  >
    <template #no-results>
      <slot name="no-results" />
    </template>
    <div class="w-full mb-0 grid grid-cols-3 gap-6">
      <div
        v-for="option in options"
        :key="option.id"
        class="rounded cursor-pointer option"
        :class="[{ selected: currentItem && option.id === currentItem.id }]"
        @click.stop.prevent="onSelected(option)"
      >
        <slot name="item" :option="option">
          <div class="p-3">
            <img class="w-full border border-gray-300 rounded" :src="option.thumbnail" />
            <div class="mt-2 text-sm break-words">{{ option.name }}</div>
          </div>
        </slot>
      </div>
      <template v-if="loading && autoLoad && options.length !== 0">
        <div class="w-full p-4 text-center">
          <FontAwesomeIcon class="text-gray-300" icon="circle-notch" spin="spin" size="2x" />
        </div>
      </template>
    </div>
  </VSearchable>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'

export interface Option {
  id: string | number
  name: string
  thumbnail: string
}

defineProps({
  options: {
    type: Array as PropType<Option[]>,
    required: true,
  },
  currentItem: {
    type: Object,
    default: null,
  },
  count: {
    type: Number,
    required: true,
  },
  placeholder: {
    type: String,
    default: 'Search\u2026',
  },
  search: {
    type: String as PropType<string | null>,
    default: '',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  autoLoad: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['selected', 'update:search', 'scroll', 'load-more'])

function onSelected(option: Option) {
  emit('selected', option)
}
</script>

<style lang="stylus" scoped>
.v-searchable-thumbnail-list
  .option img
    aspect-ratio 16/9
    object-fit cover
  .selected
    outline 2px solid theme('colors.copper.700')
    outline-offset -2px
    @apply bg-copper-100 text-copper-800 font-semibold
</style>
